import React, { useEffect, useState } from 'react'
import { IUser } from './types'
import { FaEdit, FaLock, FaUnlock } from 'react-icons/fa' 
import { IoKeyOutline } from "react-icons/io5"
import useFetch from '../../_fetch'
import { useAuth } from '../../auth/auth-context'
import DataTable, { TableColumn } from 'react-data-table-component'
import UserModal from './user-modal'
import ResetPasswordModal from './reset-password-modal'
import LockUnlockModal from './lock-unlock-modal'
import { Button, InputGroup, FormControl } from 'react-bootstrap'
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa"
import { FaTrash } from 'react-icons/fa'
import DeleteUserModal from './delete-user-modal'

const UserManagement: React.FC = () => {
    const { user } = useAuth()
    const { doFetch } = useFetch()
    const [users, setUsers] = useState<IUser[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [modalVisible, setModalVisible] = useState<boolean>(false)
    const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState<boolean>(false)
    const [lockUnlockModalVisible, setLockUnlockModalVisible] = useState<boolean>(false)
    const [currentUser, setCurrentUser] = useState<IUser | null>(null) 
    const [isLocked, setIsLocked] = useState<boolean>(false)
    const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false)

    useEffect(() => {
        (async () => {
            try {
                const res = await doFetch('/user/get-users')
                if (res.status === 200) {
                    const data = await res.json()
                    setUsers(data)
                }
            } catch (err) {
                setError('Failed to load users.')
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    if (!user?.isAdmin) return null

    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const renderAccountStatus = (row: IUser) => {
        if (row.loginAttempts && row.loginAttempts > 5) {
            return (
                <span className="badge bg-danger">
                    Locked
                </span>
            )
        }
        
        const now = Date.now()
        const expiration = row.expirationDate
            ? new Date(row.expirationDate).getTime()
            : null

        if (row.isVerified === false) {
            return (
                <div>
                    <span className="badge bg-warning text-dark">
                        Unverified
                    </span>

                    {expiration && (
                        <div className="text-muted small">
                            Expires {new Date(expiration).toLocaleDateString()}
                        </div>
                    )}
                </div>
            )
        }

        if (expiration) {
            if (expiration < now) {
                return (
                    <span className="badge bg-danger">
                        Trial Expired
                    </span>
                )
            }

            return (
                <div>
                    <span className="badge bg-info text-dark">
                        Trial
                    </span>
                    <div className="text-muted small">
                        Expires {new Date(expiration).toLocaleDateString()}
                    </div>
                </div>
            )
        }

        return (
            <span className="badge bg-success">
                Active
            </span>
        )
    }




    const columns: TableColumn<IUser>[] = [
        {
            selector: (row: IUser) => row.id,
            sortable: true,
            omit: true,
            id:'id',
        },
        {
            name: 'Username',
            selector: (row: IUser) => row.username,
            cell: (row: IUser) => <b>{row.username}</b>,
            sortable: true,
        },
        {
            name: 'Admin',
            selector: (row: IUser) => (row.isAdmin ? 'Yes' : 'No'),
            sortable: true,
        },
        {
            name: 'Kind',
            selector: (row: IUser) => row.kind,
            sortable: true,
        },
        {
            name: 'Feed',
            cell: (row: IUser) => (
                row.feed?.disabled 
                    ? <FaTimesCircle className="text-danger" size={14} /> 
                    : <FaCheckCircle className="text-success" size={14} />
            ),
            sortable: true,
        },
        {
            name: 'Marketing',
            cell: (row: IUser) => (
                row.marketing?.disabled 
                    ? <FaTimesCircle className="text-danger" size={14} /> 
                    : <FaCheckCircle className="text-success" size={14} />
            ),
            sortable: true,
        },
        {
            name: "ByeLink",
            cell: (row: IUser) => (
                row.byClick?.enabled
                    ? <FaCheckCircle className="text-success" size={14} />
                    : <FaTimesCircle className="text-danger" size={14} />
            ),
            sortable: true,
        },
        {
            name: 'Account Status',
            cell: renderAccountStatus,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: (row: IUser) => (
                <div>
                    <FaEdit
                        className="text-primary me-2"
                        style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                        onClick={() => handleEdit(row)}
                    />
                    {row.loginAttempts && row.loginAttempts > 5 ? (
                        <FaUnlock
                            className="text-success me-2"
                            style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                            onClick={() => handleLockUnlock(row, true)}
                        />
                    ) : (
                        <FaLock
                            className="text-danger me-2"
                            style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                            onClick={() => handleLockUnlock(row, false)}
                        />
                    )}
                    <IoKeyOutline
                        className="text-info me-2"
                        style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                        onClick={() => handleResetPassword(row)}
                    />
                    <FaTrash
                        className="text-danger"
                        style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                        onClick={() => handleDeleteUser(row)}
                    />
                </div>

            ),
        },
    ]

    const handleEdit = (user: IUser) => {
        setCurrentUser(user)
        setModalVisible(true)
    }

    const handleCreateUser = () => {
        setCurrentUser(null)
        setModalVisible(true)
    }

    const handleLockUnlock = (user: IUser, unlock: boolean) => {
        setCurrentUser(user)
        setIsLocked(unlock)
        setLockUnlockModalVisible(true)
    }

    const handleResetPassword = (user: IUser) => {
        setCurrentUser(user)
        setResetPasswordModalVisible(true)
    }

    const handleDeleteUser = (user: IUser) => {
        setCurrentUser(user)
        setDeleteUserModalVisible(true)
    }

    const handleDeleteUserSubmit = async () => {
        if (!currentUser) return

        const res = await doFetch('/remove-user-full', {
            username: currentUser.username,
        })

        if (res.status !== 200) {
            throw await res.text()
        }

        setUsers(prev =>
            prev.filter(u => u.id !== currentUser.id)
        )

        setDeleteUserModalVisible(false)
        setCurrentUser(null)
    }



    const handleLockUnlockSubmit = async (unlock: boolean) => {
        if (currentUser) {
            const kind = unlock ? 'unlock' : 'lock'
            const res = await doFetch(`/user/account-lock`, {
                username: currentUser.username,
                kind,
            })
            if (res.status !== 200) throw `Something went wrong: ${await res.text()}`

            const updatedUsers = users.map(user => {
                if (user.id === currentUser.id) {
                    return {
                        ...user,
                        loginAttempts: unlock ? 0 : 10,
                    }
                }
                return user
            })

            setUsers(updatedUsers)
        }
        setLockUnlockModalVisible(false)
    }

    const handlePasswordSubmit = async (newPassword: string) => {
        if (currentUser) {
            const res = await doFetch('/user/reset-password', {
                username: currentUser.username,
                password: newPassword,
            })
            if(res.status !== 200) throw `Something went wrong: ${await res.text()}`
        }
        setResetPasswordModalVisible(false)
    }

    const handleCloseModal = () => {
        setModalVisible(false)
        setCurrentUser(null)
    }

    if (loading) {
        return (
            <div className="container mt-4 d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <div className="container mt-4 text-danger">{error}</div>
    }

    return (
        <div className="container mt-4">
            <Button variant="primary" className="mb-3" onClick={handleCreateUser}>
                Create User
            </Button>

            <InputGroup className="mb-3">
                <FormControl
                    type="text"
                    placeholder="Search by username"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            <DataTable
                columns={columns}
                data={filteredUsers}
                pagination
                paginationPerPage={20}
                defaultSortFieldId="id"
                defaultSortAsc={false}
                striped
                highlightOnHover
                progressPending={loading}
                progressComponent={
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                }
            />

            <UserModal
                show={modalVisible}
                onHide={handleCloseModal}
                currentUser={currentUser}
                onSubmit={async (user, logo) => {
                    const url = currentUser ? '/user/update' : '/user/create'
                    
                    if (currentUser) (user as any).id = currentUser.id
                    const formData = new FormData()
                    formData.append('logo', logo || '')
                    formData.append('user', JSON.stringify(user))
                    const res = await doFetch(url, formData)
                    if (res.status !== 200) throw await res.text()

                    const updatedUser: IUser = await res.json()
                    if (currentUser) {
                        const userIndex = users.findIndex((u) => u.id === currentUser.id)
                        users[userIndex] = { ...updatedUser, loginAttempts: currentUser.loginAttempts }
                    } else {
                        users.push(updatedUser)
                    }

                    setUsers(users)
                }}
            />

            <ResetPasswordModal
                show={resetPasswordModalVisible}
                onHide={() => setResetPasswordModalVisible(false)}
                onSubmit={handlePasswordSubmit}
            />

            <LockUnlockModal
                show={lockUnlockModalVisible}
                onHide={() => setLockUnlockModalVisible(false)}
                onSubmit={handleLockUnlockSubmit}
                isLocked={isLocked}
            />

            <DeleteUserModal
                show={deleteUserModalVisible}
                onHide={() => setDeleteUserModalVisible(false)}
                onSubmit={handleDeleteUserSubmit}
                username={currentUser?.username}
            />

        </div>
    )
}

export default UserManagement
